import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, ShieldCheck, Clock } from "lucide-react";
import { OptionsManager } from "../components/OptionsManager";
import { ColorManager } from "../components/ColorManager";
import { ManageAgents } from "../components/ManageAgents";
import { AdminResetPassword } from "../components/AdminResetPassword";

const PERMS = [
  { key: "view", label: "View" },
  { key: "edit", label: "Create/Edit" },
  { key: "status", label: "Status/Bulk" },
  { key: "delete", label: "Delete" },
  { key: "statistics", label: "Statistics" },
  { key: "agents", label: "Agents" },
];

export default function AdminPortal() {
  const { user: me, isMaster } = useAuth();
  const [users, setUsers] = useState([]);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (u, body) => {
    try {
      const { data } = await api.put(`/users/${u.id}`, body);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? data : x)));
      toast.success("User updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const toggleActive = (u, val) => patch(u, { active: val });
  const togglePerm = (u, key, val) =>
    patch(u, { permissions: { ...u.permissions, [key]: val } });
  const setRole = (u, role) => patch(u, { role });

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("User removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const reps = users.filter((u) => u.role === "rep");
  const admins = users.filter((u) => u.role === "admin" || u.role === "master_admin");

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Administration</p>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Admin Portal</h1>
        <p className="text-sm text-muted-foreground mt-2">Approve rep accounts and control what each rep can access.</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Total Users</div>
          <div className="font-head text-3xl font-extrabold">{users.length}</div>
        </div>
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Pending Approval</div>
          <div className="font-head text-3xl font-extrabold text-amber-600">{reps.filter((r) => !r.active).length}</div>
        </div>
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Active Reps</div>
          <div className="font-head text-3xl font-extrabold text-green-600">{reps.filter((r) => r.active).length}</div>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto mb-6">
        <div className="px-5 py-3.5 border-b border-border"><h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Reps</h2></div>
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60 hover:bg-secondary/60">
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Rep</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Approved</TableHead>
              {PERMS.map((p) => (
                <TableHead key={p.key} className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold text-center">{p.label}</TableHead>
              ))}
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reps.length === 0 && (
              <TableRow><TableCell colSpan={PERMS.length + 3} className="text-center py-10 text-muted-foreground text-sm">No rep accounts yet. Reps can register from the login screen.</TableCell></TableRow>
            )}
            {reps.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.email}`} className="border-border">
                <TableCell>
                  <div className="text-sm font-medium">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.email}</div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch data-testid={`approve-${u.email}`} checked={u.active} onCheckedChange={(v) => toggleActive(u, v)} />
                    {u.active ? <span className="text-xs text-green-600 flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Active</span>
                      : <span className="text-xs text-amber-600 flex items-center gap-1"><Clock className="w-3 h-3" />Pending</span>}
                  </div>
                </TableCell>
                {PERMS.map((p) => (
                  <TableCell key={p.key} className="text-center">
                    <Switch data-testid={`perm-${p.key}-${u.email}`} checked={Boolean(u.permissions?.[p.key])}
                      disabled={!u.active} onCheckedChange={(v) => togglePerm(u, p.key, v)} />
                  </TableCell>
                ))}
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="rounded-sm h-8 text-xs" data-testid={`promote-${u.email}`}
                      onClick={() => setRole(u, "admin")}>Make Admin</Button>
                    <AdminResetPassword user={u} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`delete-user-${u.email}`} className="rounded-sm h-8 text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="rounded-sm bg-card">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove {u.name}?</AlertDialogTitle>
                          <AlertDialogDescription>This permanently deletes their account and access.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                          <AlertDialogAction className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={() => remove(u)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border"><h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Admins</h2></div>
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60 hover:bg-secondary/60">
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Admin</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {admins.map((u) => (
              <TableRow key={u.id} className="border-border">
                <TableCell>
                  <div className="text-sm font-medium flex items-center gap-2">
                    {u.name} {u.id === me?.id && <span className="text-xs text-muted-foreground">(you)</span>}
                    {u.role === "master_admin" && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border border-amber-300 bg-amber-100 text-amber-700">Master Admin</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{u.email} · {u.role === "master_admin" ? "overrules all admins" : "full access"}</div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.id !== me?.id && (u.role !== "master_admin" || isMaster) && <AdminResetPassword user={u} />}
                    {u.id !== me?.id && u.role !== "master_admin" && (
                      <Button variant="ghost" size="sm" className="rounded-sm h-8 text-xs" data-testid={`demote-${u.email}`}
                        onClick={() => setRole(u, "rep")}>Revoke Admin</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ManageAgents readOnly={!isMaster} />
      <OptionsManager readOnly={!isMaster} />
      <ColorManager />
    </div>
  );
}
